import { Wizard, type WizardStep } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import { entries } from "@mixitone/util";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { ValueComponent } from "components/Forms/FormValueComponents";
import {
  FormatSettingsFields,
  TournamentCompetitionFormController,
} from "components/Forms/TournamentCompetitionForm";
import WizardModal from "components/common/WizardModal";
import { injectModal } from "lib/injectParams";
import { useMemo } from "react";
import "react-day-picker/dist/style.css";
import type { SchemaSettings } from "tournament-formats/TournamentFormat";

const ControlledNewCompetitionWizard = () => {
  const controller = TournamentCompetitionFormController.use();
  const { record, saving, loading } = controller.state;
  const fields = useMemo(() => controller.fields(), []);

  if (loading) return <WizardModal />;

  const stepFields = {
    type: [fields.entry_type],
    format: [fields.format],
    formatSettings: [], // Placeholder for FormatSettingsFields component
    details: [fields.name, fields.min_entries, fields.max_entries],
  };
  const getValueComponent = (field: FormFieldConfig, i: number) => (
    <ValueComponent field={field} record={record} key={i} />
  );

  const validateFields = (fields: FormFieldConfig[]) => {
    return () =>
      fields.every((field) => {
        const value = (record as unknown as Record<string, unknown>)[field.name];
        return !field.required || value;
      });
  };

  const steps: WizardStep[] = [
    {
      name: "Type",
      title: "Let's start making a new competition",
      subtitle: "Will this be for singles or doubles?",
      content: <>{stepFields.type.map(getValueComponent)}</>,
      validate: validateFields(stepFields.type),
    },
    {
      name: "Format",
      title: "How should the competition be structured?",
      subtitle: "Choose a format that best suits your tournament style. You can change this later",
      content: <>{stepFields.format.map(getValueComponent)}</>,
      validate: validateFields(stepFields.format),
    },
    {
      name: "Settings",
      title: "Configure format settings",
      subtitle: "Customize how your competition will work. You can change this later",
      content: controller.state.formatSchema && (
        <>
          <FormatSettingsFields
            schema={controller.state.formatSchema}
            settings={controller.state.formatSettings}
            onChange={(newSettings: SchemaSettings<any>) => {
              controller.state.formatSettings = newSettings;
            }}
          />
          {entries(controller.state.formatSchema).filter(([_, schema]) => !schema.hidden).length === 0 && (
            <p className="text-center text-sm text-gray-500">No settings available for this format</p>
          )}
        </>
      ),
    },
    {
      name: "Details",
      title: "Almost done!",
      subtitle: "Give your competition a name and set the player limits",
      content: (
        <>
          {stepFields.details.map((field, i) => (
            <div className="flex flex-col gap-2" key={i}>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: old */}
              <label className="text-sm text-gray-600">{field.label}</label>
              <ValueComponent field={field} record={record} autoFocus={i === 0} />
            </div>
          ))}
        </>
      ),
      validate: validateFields(stepFields.details),
    },
  ];

  return (
    <WizardModal>
      <Wizard
        steps={steps}
        onComplete={() => controller.actionSubmit()}
        saving={saving}
        doneButtonText="Create competition"
      />
    </WizardModal>
  );
};

const NewCompetitionWizard = TournamentCompetitionFormController.scope(
  ApplicationView(ControlledNewCompetitionWizard),
);

const NewCompetitionModal: React.FC<{ id: string }> = ({ id }) => {
  return <NewCompetitionWizard attributes={{ tournament_session_id: id }} />;
};

export default injectModal(ApplicationView(NewCompetitionModal));
